import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useEffect, useMemo, useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDInput from "@/components/atoms/MDInput";
import MDTypography from "@/components/atoms/MDTypography";
import { formatCurrency } from "@/helpers/currency-helpers";
import IProduct from "@/interfaces/product.interface";
import { ISku } from "@/interfaces/sku.interface";
import { ItemDataTabMessages } from "@/messages/order-form-page/item-data-tab.messages";

import { CampaignCouponError, resolveCampaignCouponForSku } from "../../form-data";
import ProductAutocomplete from "./ProductAutocomplete";
import useCampaignCouponPrice from "./use-campaign-coupon";
import {
  findMatchingSku,
  getSkuStock,
  getSkuUnitPrice,
  getVariationOptions,
  hasSkuStock,
  productHasStock,
  useProductSkus,
  variationOptionHasStock,
} from "./use-product-skus";

type StockError = { field: "product" | "variation" | "quantity"; message: string };

export type NewProductPayload = {
  sku: ISku;
  product: IProduct;
  quantity: number;
  discountCouponCode: string;
  campaignId?: number | null;
  campaignNetUnitPrice?: number | null;
};

interface Props {
  open: boolean;
  messages: ItemDataTabMessages;
  existingSkuCodes: string[];
  campaignId?: number | null;
  contactId?: number | null;
  initialCouponCode?: string;
  onClose: () => void;
  onConfirm: (payload: NewProductPayload) => void;
}

const NewProductDialog: React.FC<Props> = ({
  open,
  messages,
  existingSkuCodes,
  campaignId,
  contactId,
  initialCouponCode,
  onClose,
  onConfirm,
}) => {
  const [product, setProduct] = useState<IProduct | null>(null);
  const [selectedVariations, setSelectedVariations] = useState<Record<string, string>>({});
  const [quantity, setQuantity] = useState<string>("1");
  const [discountCouponCode, setDiscountCouponCode] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [stockError, setStockError] = useState<StockError | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { skus, variationNames, isLoading } = useProductSkus({ productId: product?.id });

  const clearErrors = () => {
    setError(null);
    setStockError(null);
  };

  useEffect(() => {
    if (!open) {
      setProduct(null);
      setSelectedVariations({});
      setQuantity("1");
      setError(null);
      setStockError(null);
      setIsSaving(false);
      return;
    }

    setDiscountCouponCode(initialCouponCode?.trim() || "");
  }, [open, initialCouponCode]);

  useEffect(() => {
    setSelectedVariations({});
    setError(null);
    setStockError(null);
  }, [product?.id]);

  const selectedSku = useMemo(
    () => findMatchingSku(skus, variationNames, selectedVariations),
    [skus, variationNames, selectedVariations]
  );

  const skuUnitPrice = selectedSku ? getSkuUnitPrice(selectedSku) : 0;
  const skuStock = selectedSku ? getSkuStock(selectedSku) : 0;
  const numericQuantity = Number(quantity.replace(",", ".")) || 0;
  const exceedsStock = !!selectedSku && skuStock > 0 && numericQuantity > skuStock;
  const quantityExceedsStockText = messages.quantityExceedsStock.replace("{stock}", String(skuStock));
  const variationsWithoutStock = variationNames.filter(
    (name) =>
      !!selectedVariations[name] &&
      !variationOptionHasStock(skus, name, selectedVariations[name], selectedVariations, variationNames)
  );
  const trimmedCoupon = discountCouponCode.trim();
  const {
    unitPrice,
    isLoading: isCouponLoading,
    error: couponError,
    minimumQuantity,
  } = useCampaignCouponPrice({
    sku: selectedSku,
    quantity: numericQuantity,
    campaignId,
    couponCode: discountCouponCode,
    contactId,
    skuUnitPrice,
  });
  const totalValue = unitPrice * numericQuantity;
  const couponHelperText = getCouponHelperText(
    messages,
    couponError,
    minimumQuantity,
    isCouponLoading && Boolean(trimmedCoupon || campaignId)
  );
  const isValid =
    !!product &&
    !isLoading &&
    numericQuantity > 0 &&
    !isCouponLoading &&
    !isSaving &&
    !(trimmedCoupon && couponError);

  const handleVariationChange = (name: string, value: string | null) => {
    clearErrors();
    setSelectedVariations((current) => {
      const next: Record<string, string> = { ...current, [name]: value || "" };
      // limpa as variações seguintes, pois as opções dependem das anteriores
      const index = variationNames.indexOf(name);
      variationNames.slice(index + 1).forEach((following) => {
        next[following] = "";
      });
      return next;
    });
  };

  const handleConfirm = async () => {
    if (!product || isLoading || isSaving) return;

    if (!productHasStock({ skus })) {
      setStockError({ field: "product", message: messages.productOutOfStock });
      return;
    }

    if (!selectedSku) {
      setError(messages.selectVariationRequired);
      return;
    }

    if (numericQuantity <= 0) {
      setError(messages.quantityMoreThanZero);
      return;
    }

    if (!hasSkuStock(selectedSku)) {
      setStockError({
        field: variationNames.length > 0 ? "variation" : "product",
        message: messages.skuOutOfStock,
      });
      return;
    }

    if (numericQuantity > getSkuStock(selectedSku)) {
      setStockError({ field: "quantity", message: quantityExceedsStockText });
      return;
    }

    if (existingSkuCodes.includes(String(selectedSku.code))) {
      setError(messages.skuAlreadyAdded);
      return;
    }

    setIsSaving(true);
    try {
      const lookup = await resolveCampaignCouponForSku({
        sku: selectedSku,
        quantity: numericQuantity,
        campaignId,
        couponCode: trimmedCoupon,
        contactId,
      });

      if (trimmedCoupon && lookup.error) {
        setError(getCouponErrorMessage(messages, lookup.error, lookup.minimumQuantity));
        return;
      }

      onConfirm({
        sku: selectedSku,
        product,
        quantity: numericQuantity,
        discountCouponCode: trimmedCoupon,
        campaignId: lookup.campaign?.id ?? campaignId ?? null,
        campaignNetUnitPrice: lookup.unitPrice,
      });
      onClose();
    } catch {
      setError(messages.couponInvalid);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <MDTypography variant="h5" fontWeight="bold">
          {messages.newProductDialogTitle}
        </MDTypography>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2.5} pt={1}>
          <Grid size={12}>
            <ProductAutocomplete
              messages={messages}
              value={product}
              onChange={(selected) => {
                clearErrors();
                setProduct(selected);
              }}
              errorText={stockError?.field === "product" ? stockError.message : null}
            />
          </Grid>

          {variationNames.map((name) => {
            const options = getVariationOptions(skus, name, selectedVariations, variationNames);
            const isWithoutStock = variationsWithoutStock.includes(name);
            const showStockError =
              stockError?.field === "variation" &&
              name === (variationsWithoutStock[0] ?? variationNames[variationNames.length - 1]);
            return (
              <Grid size={12} key={name}>
                <Autocomplete
                  fullWidth
                  value={selectedVariations[name] || null}
                  onChange={(_, value) => handleVariationChange(name, value)}
                  options={options}
                  loading={isLoading}
                  noOptionsText={messages.noOptionsItems}
                  renderOption={(props, option) => (
                    <li {...props} key={option}>
                      <MDBox display="flex" width="100%" gap={1.5} alignItems="center">
                        <MDTypography variant="caption" sx={{ flex: 1 }}>
                          {option}
                        </MDTypography>
                        {!variationOptionHasStock(skus, name, option, selectedVariations, variationNames) && (
                          <MDTypography variant="caption" color="error" fontWeight="medium" noWrap>
                            {messages.outOfStockLabel}
                          </MDTypography>
                        )}
                      </MDBox>
                    </li>
                  )}
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      variant="outlined"
                      label={name}
                      error={showStockError}
                      helperText={
                        showStockError
                          ? stockError?.message
                          : isWithoutStock
                            ? messages.outOfStockLabel
                            : ""
                      }
                      FormHelperTextProps={{ sx: { color: "error.main" } }}
                    />
                  )}
                />
              </Grid>
            );
          })}

          <Grid size={12}>
            <MDInput
              fullWidth
              variant="outlined"
              type="number"
              label={messages.quantityColumn}
              value={quantity}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                clearErrors();
                setQuantity(e.target.value);
              }}
              error={
                (quantity !== "" && numericQuantity <= 0) ||
                exceedsStock ||
                stockError?.field === "quantity"
              }
              helperText={
                quantity !== "" && numericQuantity <= 0
                  ? messages.quantityMoreThanZero
                  : exceedsStock || stockError?.field === "quantity"
                    ? quantityExceedsStockText
                    : ""
              }
              slotProps={{
                htmlInput: { min: 1, step: 1, ...(skuStock > 0 ? { max: skuStock } : {}) },
              }}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              variant="outlined"
              label={messages.discountCouponLabel}
              value={discountCouponCode}
              onChange={(e) => {
                setError(null);
                setDiscountCouponCode(e.target.value);
              }}
              error={Boolean(trimmedCoupon && couponError && !isCouponLoading)}
              helperText={couponHelperText}
              slotProps={{ htmlInput: { maxLength: 50 } }}
            />
          </Grid>

          <Grid size={12}>
            <Divider sx={{ my: 0.5 }} />
          </Grid>

          <Grid size={12}>
            <MDBox display="flex" justifyContent="space-between" mb={1}>
              <MDTypography variant="button" color="text">
                {messages.stockBalanceLabel}
              </MDTypography>
              <MDTypography variant="button" fontWeight="bold">
                {selectedSku ? String(skuStock) : "-"}
              </MDTypography>
            </MDBox>

            <MDBox display="flex" justifyContent="space-between" mb={1}>
              <MDTypography variant="button" color="text">
                {messages.netUnitPriceColumn}
              </MDTypography>
              <MDTypography variant="button" fontWeight="bold">
                {selectedSku ? formatCurrency(unitPrice) : "-"}
              </MDTypography>
            </MDBox>

            <MDBox display="flex" justifyContent="space-between">
              <MDTypography variant="button" color="text">
                {messages.totalValueLabel}
              </MDTypography>
              <MDTypography variant="button" fontWeight="bold">
                {selectedSku ? formatCurrency(totalValue) : "-"}
              </MDTypography>
            </MDBox>
          </Grid>

          {error && (
            <Grid size={12}>
              <MDTypography variant="caption" color="error" fontWeight="medium">
                {error}
              </MDTypography>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <MDButton variant="outlined" color="secondary" onClick={onClose}>
          {messages.cancelButton}
        </MDButton>
        <MDButton variant="gradient" color="dark" disabled={!isValid} onClick={handleConfirm}>
          {messages.saveButton}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

function getCouponErrorMessage(
  messages: ItemDataTabMessages,
  error: CampaignCouponError,
  minimumQuantity: number | null
): string {
  if (error === "minQuantity") {
    return messages.couponMinQuantity.replace("{minQuantity}", String(minimumQuantity ?? 1));
  }
  if (error === "notApplicable") return messages.couponNotApplicable;
  return messages.couponInvalid;
}

function getCouponHelperText(
  messages: ItemDataTabMessages,
  error: CampaignCouponError | null,
  minimumQuantity: number | null,
  isLoading: boolean
): string {
  if (isLoading) return messages.validatingCoupon;
  if (!error) return "";
  return getCouponErrorMessage(messages, error, minimumQuantity);
}

export default NewProductDialog;
